/**
 * @module Utils.Mp4Util
 */
function createMp4Util(logService, util) {
    const _mp4Util = {};

    const handlerTypes = Object.freeze({
        soun: 'soun',
        vide: 'vide'
    });

    const boxTypes = Object.freeze({
        aART: 'aART',
        assp: 'assp',
        avc1: 'avc1',
        avcC: 'avcC',
        bxml: 'bxml',
        catg: 'catg',
        co64: 'co64',
        colr: 'colr',
        covr: 'covr',
        cprt: 'cprt',
        cslg: 'cslg',
        ctts: 'ctts',
        data: 'data',
        desc: 'desc',
        dinf: 'dinf',
        dref: 'dref',
        edts: 'edts',
        elng: 'elng',
        elst: 'elst',
        esds: 'esds',
        fecr: 'fecr',
        fiel: 'fiel',
        fiin: 'fiin',
        fpar: 'fpar',
        free: 'free',
        frma: 'frma',
        ftyp: 'ftyp',
        gitn: 'gitn',
        gnre: 'gnre',
        hdlr: 'hdlr',
        hint: 'hint',
        hmhd: 'hmhd',
        htcb: 'htcb',
        idat: 'idat',
        iinf: 'iinf',
        iloc: 'iloc',
        ilst: 'ilst',
        iods: 'iods',
        ipmc: 'ipmc',
        ipro: 'ipro',
        iref: 'iref',
        keyw: 'keyw',
        mdat: 'mdat',
        mdhd: 'mdhd',
        mdia: 'mdia',
        mdir: 'mdir',
        meco: 'meco',
        mehd: 'mehd',
        mere: 'mere',
        meta: 'meta',
        mfhd: 'mfhd',
        mfra: 'mfra',
        mfro: 'mfro',
        minf: 'minf',
        moof: 'moof',
        moov: 'moov',
        mp4a: 'mp4a',
        mvex: 'mvex',
        mvhd: 'mvhd',
        nmhd: 'nmhd',
        padb: 'padb',
        paen: 'paen',
        pasp: 'pasp',
        pdin: 'pdin',
        pitm: 'pitm',
        prft: 'prft',
        saio: 'saio',
        saiz: 'saiz',
        sbgp: 'sbgp',
        schi: 'schi',
        schm: 'schm',
        sdtp: 'sdtp',
        segr: 'segr',
        sgpd: 'sgpd',
        sidx: 'sidx',
        sinf: 'sinf',
        skip: 'skip',
        smhd: 'smhd',
        soar: 'soar',
        sonm: 'sonm',
        ssix: 'ssix',
        stbl: 'stbl',
        stco: 'stco',
        stdp: 'stdp',
        sthd: 'sthd',
        stsc: 'stsc',
        stsd: 'stsd',
        stsh: 'stsh',
        stss: 'stss',
        stsz: 'stsz',
        stts: 'stts',
        styp: 'styp',
        stz2: 'stz2',
        subs: 'subs',
        tfdt: 'tfdt',
        tfhd: 'tfhd',
        tfra: 'tfra',
        tgas: 'tgas',
        tkhd: 'tkhd',
        traf: 'traf',
        trak: 'trak',
        trep: 'trep',
        trex: 'trex',
        trgr: 'trgr',
        trkn: 'trkn',
        trun: 'trun',
        tx3g: 'tx3g',
        udta: 'udta',
        url : 'url ',
        urn : 'urn ',
        uuid: 'uuid',
        vmhd: 'vmhd',
        wide: 'wide',
        xml : 'xml ',
        '©alb': '©alb',
        '©ART': '©ART',
        '©art': '©art',
        '©aut': '©aut',
        '©cmt': '©cmt',
        '©day': '©day',
        '©des': '©des',
        '©gen': '©gen',
        '©grp': '©grp',
        '©nam': '©nam',
        '©TIM': '©TIM',
        '©too': '©too',
        '©TSC': '©TSC',
        '©TSZ': '©TSZ'
    });

    const avcProfilesWithExtraInfo = ',64,6E,7A,F4,2C,53,56,76,80,';

    const audioObjectTypes = Object.freeze({
        '0':  'Null',
        '1':  'AAC Main',
        '2':  'AAC LC (Low Complexity)',
        '3':  'AAC SSR (Scalable Sample Rate)',
        '4':  'AAC LTP (Long Term Prediction)',
        '5':  'SBR (Spectral Band Replication)',
        '6':  'AAC Scalable',
        '7':  'TwinVQ',
        '8':  'CELP (Code Excited Linear Prediction)',
        '9':  'HXVC (Harmonic Vector eXcitation Coding)',
        '10': 'Reserved',
        '11': 'Reserved',
        '12': 'TTSI (Text-To-Speech Interface)',
        '13': 'Main Synthesis',
        '14': 'Wavetable Synthesis',
        '15': 'General MIDI',
        '16': 'Algorithmic Synthesis and Audio Effects',
        '17': 'ER (Error Resilient) AAC LC',
        '18': 'Reserved',
        '19': 'ER AAC LTP',
        '20': 'ER AAC Scalable',
        '21': 'ER TwinVQ',
        '22': 'ER BSAC (Bit-Sliced Arithmetic Coding)',
        '23': 'ER AAC LD (Low Delay)',
        '24': 'ER CELP',
        '25': 'ER HVXC',
        '26': 'ER HILN (Harmonic and Individual Lines plus Noise)',
        '27': 'ER Parametric',
        '28': 'SSC (SinuSoidal Coding)',
        '29': 'PS (Parametric Stereo)',
        '30': 'MPEG Surround',
        '31': '(Escape value)',
        '32': 'Layer-1',
        '33': 'Layer-2',
        '34': 'Layer-3',
        '35': 'DST (Direct Stream Transfer)',
        '36': 'ALS (Audio Lossless)',
        '37': 'SLS (Scalable LosslesS)',
        '38': 'SLS non-core',
        '39': 'ER AAC ELD (Enhanced Low Delay)',
        '40': 'SMR (Symbolic Music Representation) Simple',
        '41': 'SMR Main',
        '42': 'USAC (Unified Speech and Audio Coding) (no SBR)',
        '43': 'SAOC (Spatial Audio Object Coding)',
        '44': 'LD MPEG Surround',
        '45': 'USAC'
    });

    const sampleRates = Object.freeze({
        '0':  '96000 Hz',
        '1':  '88200 Hz',
        '2':  '64000 Hz',
        '3':  '48000 Hz',
        '4':  '44100 Hz',
        '5':  '32000 Hz',
        '6':  '24000 Hz',
        '7':  '22050 Hz',
        '8':  '16000 Hz',
        '9':  '12000 Hz',
        '10': '11025 Hz',
        '11': '8000 Hz',
        '12': '7350 Hz',
        '13': 'Reserved',
        '14': 'Reserved',
        '15': 'frequency is written explictly'
    });

    /**
     * {@link https://developer.apple.com/library/archive/documentation/QuickTime/QTFF/Metadata/Metadata.html#//apple_ref/doc/uid/TP40000939-CH1-SW34}
     */
    const dataTypes = Object.freeze({
        '0':  'binary',
        '1':  'UTF-8',
        '2':  'UTF-16',
        '3':  'S/JIS',
        '13': 'JPEG',
        '14': 'PNG',
        '21': 'signed int',
        '22': 'unsigned int',
        '23': 'float 32',
        '24': 'float 64'
    });

    function log(params) {
        params.module = 'mp4Util';
        logService.log(params);
        return params;
    }

    function byType (item) {
        return item.type === this.toString();
    }

    function uInt64(hi, lo) {
        return hi * Math.pow(2, 33) + lo;
    }

    function byteToHexString(byte) {
        return ('0' + byte.toString(16).toUpperCase()).slice(-2);
    }

    function readChildrenAtPos(dataView, obj, offsetIn, mp4Obj) {
        let offset = offsetIn;
        offset ??= 0;

        function readBoxHeader(dataView, pos) {
            let size = dataView.getUint32(pos);
            const type = String.fromCharCode(dataView.getUint8(pos + 4))
                    + String.fromCharCode(dataView.getUint8(pos + 5))
                    + String.fromCharCode(dataView.getUint8(pos + 6))
                    + String.fromCharCode(dataView.getUint8(pos + 7));
            let headerSize;
            if (size === 1) {
                // result.size == 1 means that the next bits are the 64-bit size.
                size = uInt64(dataView.getUint32(pos + 8), dataView.getUint32(pos + 12));
                headerSize = 16;
            } else {
                headerSize = 8;
            }

            return {
                size: size,
                type: type,
                headerSize: headerSize,
                pos: pos
            };
        }

        let pos;
        if (obj.pos === undefined) {
            // top level only
            obj.pos = 0;
            pos = 0;
        } else {
            pos = obj.pos + offset;
        }
        obj.boxes = [];

        if (typeof obj.size !== 'number' || obj.size === 0 || typeof obj.pos !== 'number') {
            throw 'readChildren error';
        }

        let box;
        let done = false;
        while (pos < obj.pos + obj.size && !done) {
            try {
                box = readBoxHeader(dataView, pos);

                if (!boxTypes[box.type]) {
                    // stop processing if box.type has invlaid characters
                    if (dataView.getUint8(box.pos + 4) < 32
                            || dataView.getUint8(box.pos + 5) < 32
                            || dataView.getUint8(box.pos + 6) < 32
                            || dataView.getUint8(box.pos + 7) < 32 ) {
                        done = true;
                    }
                    log({
                        code: 'readChildrenAtPos',
                        descr: 'unknown box.type within object',
                        object: {
                            box: box,
                            obj: obj,
                            deviceId: mp4Obj?.event?.srcId
                        }
                    });
                }
                obj.boxes.push(box);
                pos = pos + box.size;
            } catch (error) {
                log({
                    code: 'readChildrenAtPos',
                    descr: 'error reading box header',
                    object: {
                        box: box,
                        obj: obj,
                        deviceId: mp4Obj?.event?.srcId
                    },
                    error: error
                });
                done = true;
            }
        }
    }

    function readVersionAndFlags(dataView, box) {
        box.version = dataView.getUint8(box.pos + box.headerSize);
        box.flags = [];
        box.flags[0] = dataView.getUint8(box.pos + box.headerSize + 1);
        box.flags[1] = dataView.getUint8(box.pos + box.headerSize + 2);
        box.flags[2] = dataView.getUint8(box.pos + box.headerSize + 3);
    }

    function readFtyp(dataView, ftyp) {
        ftyp.major_brand = String.fromCharCode(dataView.getUint8(ftyp.pos + 8))
            + String.fromCharCode(dataView.getUint8(ftyp.pos + 9))
            + String.fromCharCode(dataView.getUint8(ftyp.pos + 10))
            + String.fromCharCode(dataView.getUint8(ftyp.pos + 11));

        ftyp.minor_version = dataView.getUint32(ftyp.pos + 12);

        const compatLength = Math.floor((ftyp.size - 16) / 4);
        let i = 0;
        while (i < compatLength) {
            if (!Array.isArray(ftyp.compatible_brand)) {
                ftyp.compatible_brand = [];
            }
            ftyp.compatible_brand[i] = String.fromCharCode(dataView.getUint8(ftyp.pos + i * 4 + 16))
                + String.fromCharCode(dataView.getUint8(ftyp.pos + i * 4 + 17))
                + String.fromCharCode(dataView.getUint8(ftyp.pos + i * 4 + 18))
                + String.fromCharCode(dataView.getUint8(ftyp.pos + i * 4 + 19));
            i += 1;
        }
    }

    // mdat already gets done by readChildren - nothing else needs to be done for mdat for now

    function readTkhd(dataView, tkhd, mp4Obj) {
        let pos = tkhd.headerSize + tkhd.pos;

        readVersionAndFlags(dataView, tkhd);
        pos += 4;

        if (tkhd.version === 0) {
            tkhd.created = dataView.getUint32(pos);
            pos += 4;
            tkhd.modified = dataView.getUint32(pos);
            pos += 4;
            tkhd.trackId = dataView.getUint32(pos);
            pos += 4;
            tkhd.reserved1 = dataView.getUint32(pos);
            pos += 4;
            tkhd.duration = dataView.getUint32(pos);
            pos += 4;
        } else if (tkhd.version === 1) {
            tkhd.createdHi = dataView.getUint32(pos);
            pos += 4;
            tkhd.createdLo = dataView.getUint32(pos);
            pos += 4;
            tkhd.modifiedHi = dataView.getUint32(pos);
            pos += 4;
            tkhd.modifiedLo = dataView.getUint32(pos);
            pos += 4;
            tkhd.trackId = dataView.getUint32(pos);
            pos += 4;
            tkhd.reserved1 = dataView.getUint32(pos);
            pos += 4;
            tkhd.durationHi = dataView.getUint32(pos);
            pos += 4;
            tkhd.durationLo = dataView.getUint32(pos);
            pos += 4;
        } else {
            log({
                code: 'readTkhd',
                descr: 'bad tkhd version',
                object: {
                    box: tkhd,
                    deviceId: mp4Obj?.event?.srcId
                }
            });
        }

        tkhd.reserved2 = [];
        tkhd.reserved2[0] = dataView.getUint32(pos);
        pos += 4;
        tkhd.reserved2[1] = dataView.getUint32(pos);
        pos += 4;
        tkhd.layer = dataView.getUint16(pos);
        pos += 2;
        tkhd.alternateGroup = dataView.getUint16(pos);
        pos += 2;
        tkhd.volume = [];
        tkhd.volume[0] = dataView.getUint8(pos);
        pos += 1;
        tkhd.volume[1] = dataView.getUint8(pos);
        pos += 1;
        tkhd.reserved3 = dataView.getUint16(pos);
        pos += 2;
        tkhd.matrix = [];
        let i = 0;
        while (i < 36) {
            tkhd.matrix[i] = dataView.getUint8(pos);
            pos += 1;
            i += 1;
        }
        tkhd.width = [];
        tkhd.width[0] = dataView.getUint16(pos);
        pos += 2;
        tkhd.width[1] = dataView.getUint16(pos);
        pos += 2;
        tkhd.height = [];
        tkhd.height[0] = dataView.getUint16(pos);
        pos += 2;
        tkhd.height[1] = dataView.getUint16(pos);
    }

    function readMdhd(dataView, mdhd, mp4Obj) {
        let pos = mdhd.headerSize + mdhd.pos;

        readVersionAndFlags(dataView, mdhd);
        pos += 4;

        if (mdhd.version === 0) {
            mdhd.created = dataView.getUint32(pos);
            pos += 4;
            mdhd.modified = dataView.getUint32(pos);
            pos += 4;
            mdhd.timeScale = dataView.getUint32(pos);
            pos += 4;
            mdhd.duration = dataView.getUint32(pos);
            pos += 4;
        } else if (mdhd.version === 1) {
            mdhd.createdHi = dataView.getUint32(pos);
            pos += 4;
            mdhd.createdLo = dataView.getUint32(pos);
            pos += 4;
            mdhd.modifiedHi = dataView.getUint32(pos);
            pos += 4;
            mdhd.modifiedLo = dataView.getUint32(pos);
            pos += 4;
            mdhd.timeScale = dataView.getUint32(pos);
            pos += 4;
            mdhd.durationHi = dataView.getUint32(pos);
            pos += 4;
            mdhd.durationLo = dataView.getUint32(pos);
            pos += 4;
        } else {
            log({
                code: 'readMdhd',
                descr: 'bad mdhd version',
                object: {
                    box: mdhd,
                    deviceId: mp4Obj?.event?.srcId
                }
            });
        }

        mdhd.languageValue = dataView.getUint16(pos);

        // 1-bit reserved, followed by 3 groups of 5 bits
        // Each character is packed as the difference between its ASCII value and 0x60
        mdhd.language = String.fromCharCode(((mdhd.languageValue & 31744) >> 10) + 96)
                + String.fromCharCode(((mdhd.languageValue & 992) >> 5) + 96)
                + String.fromCharCode((mdhd.languageValue & 31) + 96);

        pos += 2;
        mdhd.predefined = dataView.getUint16(pos);
    }

    function readHdlr(dataView, hdlr) {
        let pos = hdlr.headerSize + hdlr.pos;

        readVersionAndFlags(dataView, hdlr);
        pos += 4;

        // note: predefined and qtType are different representations of the same data
        hdlr.predefined = dataView.getUint32(pos);
        hdlr.qtType = String.fromCharCode(dataView.getUint8(pos))
            + String.fromCharCode(dataView.getUint8(pos + 1))
            + String.fromCharCode(dataView.getUint8(pos + 2))
            + String.fromCharCode(dataView.getUint8(pos + 3));
        pos += 4;

        hdlr.handlerType = String.fromCharCode(dataView.getUint8(pos))
            + String.fromCharCode(dataView.getUint8(pos + 1))
            + String.fromCharCode(dataView.getUint8(pos + 2))
            + String.fromCharCode(dataView.getUint8(pos + 3));
        pos += 4;

        hdlr.reserved = [];
        let i = 0;
        while (i < 12) {
            hdlr.reserved[i] = dataView.getUint8(pos);
            i += 1;
            pos += 1;
        }

        // name is the remaining bytes minus an optional 0 string terminator
        let remaining = hdlr.pos + hdlr.size - pos;
        hdlr.name = '';
        i = 0;
        while (i < remaining) {
            if (dataView.getUint8(pos) !== 0) {
                hdlr.name += String.fromCharCode(dataView.getUint8(pos));
            }
            i += 1;
            pos += 1;
        }
    }

    function readVmhd(dataView, vmhd) {
        let pos = vmhd.headerSize + vmhd.pos;

        readVersionAndFlags(dataView, vmhd);
        pos += 4;

        vmhd.graphicsMode = dataView.getUint16(pos);
        pos += 2;

        vmhd.opcolor = [];
        vmhd.opcolor[0] = dataView.getUint8(pos);
        pos += 1;
        vmhd.opcolor[1] = dataView.getUint8(pos);
        pos += 1;
        vmhd.opcolor[2] = dataView.getUint8(pos);
        pos += 1;
    }

    function readSmhd(dataView, smhd) {
        let pos = smhd.headerSize + smhd.pos;

        readVersionAndFlags(dataView, smhd);
        pos += 4;

        smhd.audioBalance = dataView.getInt16(pos);
        pos += 2;

        smhd.reserved = dataView.getInt16(pos);
        pos += 2;
    }

    function readNmhd(dataView, nmhd) {
        readVersionAndFlags(dataView, nmhd);
    }

    function readDref(dataView, dref) {
        let pos = dref.headerSize + dref.pos;

        readVersionAndFlags(dataView, dref);
        pos += 4;

        dref.entryCount = dataView.getUint32(pos); // 12
        pos += 4;

        dref.url = {};
        dref.url.size = dataView.getUint32(pos); // 16
        pos += 4;

        dref.url.type = String.fromCharCode(dataView.getUint8(pos))
                + String.fromCharCode(dataView.getUint8(pos + 1))
                + String.fromCharCode(dataView.getUint8(pos + 2))
                + String.fromCharCode(dataView.getUint8(pos + 3));
        pos += 4;

        dref.url.version = dataView.getUint8(pos); // 24
        pos += 1;

        dref.url.flags = [];
        dref.url.flags[0] = dataView.getUint8(pos);
        pos += 1;
        dref.url.flags[1] = dataView.getUint8(pos);
        pos += 1;
        dref.url.flags[2] = dataView.getUint8(pos);
        pos += 1;

        // name is the remaining bytes minus an optional 0 string terminator
        const remaining = dref.size + dref.pos - pos;

        dref.name = '';
        let i = 0;
        while (i < remaining) {
            dref.name += String.fromCharCode(dataView.getUint8(pos + i));
            i += 1;
        }
    }

    function readDinf(dataView, dinf, mp4Obj) {
        readChildrenAtPos(dataView, dinf, dinf.headerSize, mp4Obj);

        const dref = dinf.boxes.find(byType, boxTypes.dref);
        if (dref) {
            readDref(dataView, dref);
        }
    }

    /* eslint-disable complexity */
    function parseSps(spsPos, dataView) {
        const sps = {};

        sps.length = dataView.getUint16(spsPos);

        // convert to array of bits
        const bits = new Uint8Array(sps.length * 8);
        let byteCounter;
        // start after the 16-bit length
        for (byteCounter = 2; byteCounter < sps.length; byteCounter += 1) {
            let byte = dataView.getUint8(spsPos + byteCounter);
            let bytePos = (byteCounter - 2) * 8;
            bits.set([(byte & 128) >> 7], bytePos);
            bits.set([(byte & 64) >> 6], bytePos + 1);
            bits.set([(byte & 32) >> 5], bytePos + 2);
            bits.set([(byte & 16) >> 4], bytePos + 3);
            bits.set([(byte & 8) >> 3], bytePos + 4);
            bits.set([(byte & 4) >> 2], bytePos + 5);
            bits.set([(byte & 2) >> 1], bytePos + 6);
            bits.set([(byte & 1)], bytePos + 7);
        }
        let bitPos = 0;

        function readBits(bitCount) {
            let result = 0;
            let i;
            for (i = 0; i < bitCount; i += 1) {
                if (bitPos >= bits.length) {
                    return;
                }
                if (bits[bitPos]) {
                    result += bits[bitPos] << (bitCount - 1 - i);
                }
                bitPos += 1;
            }
            return result;
        }

        function leadingZeros(array) {
            let result = 0;
            while (result < array.length && array[result] === 0) {
                result += 1;
            }
            return result;
        }

        function readUEG() {
            // read Exponential-Golumb bits
            let golBits = [];
            while (leadingZeros(golBits) * 2 + 1 > golBits.length) {
                const bit = readBits(1);
                if (bit === 1 || bit === 0) {
                    golBits.push(bit);
                } else {
                    break;
                }
            }

            // special case of zero value
            if (golBits.length && golBits[0] === 1) {
                return 0;
            }

            // find the absolute value from data bits
            let i;
            let result = 0;
            let length = golBits.length;
            for (i = leadingZeros(golBits); i < length; i += 1) {
                if (golBits[i]) {
                    result += golBits[i] << length - i - 1;
                }
            }

            return result;
        }

        function skipScalingList (count) {
            let lastScale = 8;
            let nextScale = 8;
            let j;
            let deltaScale;

            for (j = 0; j < count; j += 1) {
                if (nextScale !== 0) {
                    deltaScale = readUEG();
                    nextScale = (lastScale + deltaScale + 256) % 256;
                }
                lastScale = (nextScale === 0) ? lastScale : nextScale;
            }
        }

        /**
         * {@link https://hls-js-latest.netlify.com/api-docs/file/src/demux/exp-golomb.js.html}
         */
        sps.forbiddenZeroBit = readBits(1); // expected = 0
        sps.nalRefIdc = readBits(2); // expected = 3
        sps.nalUnitType = readBits(5); // expected = 7
        sps.avcProfileIndication = byteToHexString(readBits(8)); // expected 66 (0x42) for Baseline Profile
        sps.constraintSetFlag0 = readBits(1);
        sps.constraintSetFlag1 = readBits(1);
        sps.constraintSetFlag2 = readBits(1);
        sps.constraintSetFlag3 = readBits(1);
        sps.constraintSetFlag4 = readBits(1);
        sps.reserved = readBits(3);
        sps.avcLevelIndication = byteToHexString(readBits(8));

        sps.spsId = readUEG();

        if (avcProfilesWithExtraInfo.indexOf(sps.avcProfileIndication) > -1) {
            sps.chromaFormat = readUEG();
            if (sps.chromaFormat === 3) {
                sps.colorPlane = readBits(1);
            }
            sps.bitDepthLumaMinus8 = readUEG();
            sps.bitDepthChromaMinus8 = readUEG();
            sps.qpPrimeYZeroBypassFlag = readBits(1);
            sps.seqScalingMatrixPresentFlag = readBits(1);
            if (sps.seqScalingMatrixPresentFlag) {
                let scalingListCount = (sps.chromaFormat !== 3) ? 8 : 12;
                for (let i = 0; i < scalingListCount; i += 1) {
                    if (readBits(1)) { // seq_scaling_list_present_flag[ i ]
                        if (i < 6) {
                            skipScalingList(16);
                        } else {
                            skipScalingList(64);
                        }
                    }
                }
            }
        }

        sps.log2MaxFrameNumMinus4 = readUEG();
        sps.picOrderCntType = readUEG();

        if (sps.picOrderCntType === 0) {
            sps.log2MaxPicOrderCountMinus4 = readUEG();
        } else if (sps.picOrderCntType === 1) {
            sps.deltaPicOrderAlwaysZero = readBits(1);
            sps.offsetForNonRefPic = readUEG();
            sps.offsetForTopToBottomField = readUEG();
            let numRefFramesInPicOrderCntCycle = readUEG();

            for (let i = 0; i < numRefFramesInPicOrderCntCycle; i += 1) {
                readUEG();
            } // offset_for_ref_frame[ i ]
        }

        sps.maxNumRefFrames = readUEG();
        sps.gapsInFrameNumValAllowed = readBits(1);

        sps.widthInMacroblocksMinus1 = readUEG();
        sps.heightInMapUnitsMinus1 = readUEG();

        sps.frameMacroblocksOnly = readBits(1);

        if (sps.frameMacroblocksOnly) {
            sps.adaptiveFrameFieldFlag = readBits(1);
        }
        sps.direct8x8Inferernce = readBits(1);
        sps.frameCropping = readBits(1);
        if (sps.frameCropping) {
            sps.frameCropLeftOffset = readUEG();
            sps.frameCropRightOffset = readUEG();
            sps.frameCropTopOffset = readUEG();
            sps.frameCropBottomOffset = readUEG();
        } else {
            sps.frameCropLeftOffset = 0;
            sps.frameCropRightOffset = 0;
            sps.frameCropTopOffset = 0;
            sps.frameCropBottomOffset = 0;
        }

        sps.vuiParamsPresent = readBits(1);
        if (sps.vuiParamsPresent) {
            sps.aspectRatioPresent = readBits(1);
            if (sps.aspectRatioPresent) {
                const aspectRatioIdc = readBits(1);
                switch (aspectRatioIdc) {
                case 1: sps.pixelRatio = [1, 1]; break;
                case 2: sps.pixelRatio = [12, 11]; break;
                case 3: sps.pixelRatio = [10, 11]; break;
                case 4: sps.pixelRatio = [16, 11]; break;
                case 5: sps.pixelRatio = [40, 33]; break;
                case 6: sps.pixelRatio = [24, 11]; break;
                case 7: sps.pixelRatio = [20, 11]; break;
                case 8: sps.pixelRatio = [32, 11]; break;
                case 9: sps.pixelRatio = [80, 33]; break;
                case 10: sps.pixelRatio = [18, 11]; break;
                case 11: sps.pixelRatio = [15, 11]; break;
                case 12: sps.pixelRatio = [64, 33]; break;
                case 13: sps.pixelRatio = [160, 99]; break;
                case 14: sps.pixelRatio = [4, 3]; break;
                case 15: sps.pixelRatio = [3, 2]; break;
                case 16: sps.pixelRatio = [2, 1]; break;
                case 255: {
                    sps.pixelRatio = [readBits(8) << 8 | readBits(8), readBits(8) << 8 | readBits(8)];
                    break;
                }
                }
            }
        }

        sps.width = (sps.widthInMacroblocksMinus1 + 0) * 16 - 2 * (sps.frameCropLeftOffset + sps.frameCropRightOffset);
        sps.height = ( sps.heightInMapUnitsMinus1 + 0 ) * 16
            - ((sps.frameMacroblocksOnly ? 2 : 4) * ( sps.frameCropTopOffset + sps.frameCropBottomOffset));

        return sps;
    }
    /* eslint-enable complexity */

    function readAvcC(dataView, avcC, mp4Obj) {
        let pos = avcC.headerSize + avcC.pos;

        avcC.configVersion = dataView.getUint8(pos);
        pos += 1;
        avcC.avcProfileIndication = byteToHexString(dataView.getUint8(pos));
        pos += 1;
        avcC.profileCompatibility = byteToHexString(dataView.getUint8(pos));
        pos += 1;
        avcC.avcLevelIndication = byteToHexString(dataView.getUint8(pos));
        pos += 1;
        avcC.videoCodec = 'avc1.' + avcC.avcProfileIndication + avcC.profileCompatibility + avcC.avcLevelIndication;
        mp4Obj.videoCodec = avcC.videoCodec;

        if (!mp4Obj.light) {
            avcC.naluLengthSizeMinusOne = dataView.getUint8(pos) & 3;
            pos += 1;

            // SPS
            avcC.numberOfSps = dataView.getUint8(pos) & 31;
            pos += 1;
            avcC.sps = [];
            let spsCounter = 0;
            while (spsCounter < avcC.numberOfSps && pos < avcC.size + avcC.pos) {
                parseSps(pos, dataView);
                let sps = parseSps(pos, dataView);
                sps.data = '';
                sps.length = dataView.getUint16(pos);
                pos += 2;
                let spsByte = 0;
                while (spsByte < sps.length && pos < avcC.size + avcC.pos) {
                    sps.data += byteToHexString(dataView.getUint8(pos)) + ' ';
                    pos += 1;
                    spsByte += 1;
                }
                sps.data = sps.data.slice(0, -1);
                avcC.sps.push(sps);
                spsCounter += 1;
            }

            // PPS
            avcC.numberOfPps = dataView.getUint8(pos);
            pos += 1;
            avcC.pps = [];
            let ppsCounter = 0;
            while (ppsCounter < avcC.numberOfPps && pos < avcC.size + avcC.pos) {
                let pps = { data: '', length: 0 };
                pps.length = dataView.getUint16(pos);
                pos += 2;
                let ppsByte = 0;
                while (ppsByte < pps.length && pos < avcC.size + avcC.pos) {
                    pps.data += byteToHexString(dataView.getUint8(pos)) + ' ';
                    pos += 1;
                    ppsByte += 1;
                }
                pps.data = pps.data.slice(0, -1);
                avcC.pps.push(pps);
                ppsCounter += 1;
            }
        }
    }

    function readAvc1(dataView, avc1, mp4Obj) {
        readChildrenAtPos(dataView, avc1, avc1.headerSize + 78, mp4Obj);

        const avcC = avc1.boxes.find(byType, boxTypes.avcC);
        if (avcC) {
            readAvcC(dataView, avcC, mp4Obj);
        } else {
            log({
                code: 'readAvc1',
                descr: 'avcC box was not found in avc1 box at the expected position',
                object: {
                    box: avc1,
                    deviceId: mp4Obj?.event?.srcId
                }
            });
        }
    }

    function esdsHeader(dataView, pos, value) {
        if (dataView.getUint8(pos) === value) {
            const p1 = dataView.getUint8(pos + 1);
            const p2 = dataView.getUint8(pos + 2);
            const p3 = dataView.getUint8(pos + 3);

            // next three bytes have to be header values 0x80 or 0x81 or 0xFE
            return (p1 === 128 || p1 === 129 || p1 === 254)
                    && (p2 === 128 || p2 === 129 || p2 === 254)
                    && (p3 === 128 || p3 === 129 || p3 === 254);
        }
        return false;
    }

    /**
     * {@link https://wiki.multimedia.cx/index.php/MPEG-4_Audio}
     * {@link https://github.com/linearray/cog/blob/master/Frameworks/TagLib/taglib/taglib/m4a/mp4audiosampleentry.cpp}
     * {@link https://www.itscj.ipsj.or.jp/sc29/open/29view/29n2601t.pdf}
     * {@link https://github.com/harjot-oberai/MusicDNA/blob/master/app/src/main/java/org/jaudiotagger/audio/mp4/atom/Mp4EsdsBox.java}
     * @param {*} dataView
     * @param {*} esds
     * @param {*} mp4Obj
     */
    function readEsds(dataView, esds, mp4Obj) {
        let pos = esds.pos + esds.headerSize;

        // debug data
        esds.hex = '';
        let i = pos + 4;
        while (i < esds.size + esds.pos) {
            esds.hex += byteToHexString(dataView.getUint8(i)) + ' ';
            i += 1;
        }

        readVersionAndFlags(dataView, esds);
        pos += 4;

        let section3;
        let section4;
        let section5;
        i = pos;
        let header = 3;

        // find where each section starts, and headers may be 1 byte or 1 byte + 3 start code bytes
        while (i < esds.pos + esds.size - 4) {
            if (dataView.getUint8(i) === header) {
                if (header === 3) {
                    if (esdsHeader(dataView, i, 3)) {
                        section3 = i + 4;
                    } else {
                        section3 = i + 1;
                    }
                    header = 4;
                } else if (header === 4) {
                    if (esdsHeader(dataView, i, 4)) {
                        section4 = i + 4;
                    } else {
                        section4 = i + 1;
                    }
                    header = 5;
                } else if (header === 5) {
                    if (esdsHeader(dataView, i, 5)) {
                        section5 = i + 4;
                    } else {
                        section5 = i + 1;
                    }
                    break;
                }
            }
            i += 1;
        }

        if (section3) {
            pos = section3;
            esds.esDescriptorLength = dataView.getUint8(pos);
            pos += 1;

            esds.esId = dataView.getUint16(pos);
            pos += 2;

            esds.esStreamPriority = dataView.getUint8(pos);
            pos += 1;
        }

        if (section4) {
            pos = section4;
            esds.decoderDescriptorLength = dataView.getUint8(pos);
            pos += 1;

            // this value is represented as a Hex string
            // it is the second component of the codec, like the '40' in 'mp4a.40.2'
            esds.decoderId = dataView.getUint8(pos).toString(16);
            pos += 1;

            esds.decoderStreamType = dataView.getUint8(pos) >> 2;
            esds.upstream = (dataView.getUint8(pos) & 2) >> 1;
            const reserved = (dataView.getUint8(pos) & 1);
            if (reserved !== 1) {
                log({
                    code: 'readEsds',
                    descr: 'esds reserved value was expected to be 1 but was',
                    object: {
                        box: esds,
                        value: reserved,
                        deviceId: mp4Obj?.event?.srcId
                    }
                });
            }
            pos += 1;

            esds.bufferSizeDb = [];
            esds.bufferSizeDb[0] = dataView.getUint8(pos);
            pos += 1;
            esds.bufferSizeDb[1] = dataView.getUint8(pos);
            pos += 1;
            esds.bufferSizeDb[2] = dataView.getUint8(pos);
            pos += 1;

            esds.maxBitrate = dataView.getUint32(pos);
            pos += 4;

            // aveBitrate is 0 for variable bitrate
            esds.aveBitrate = dataView.getUint32(pos);
            pos += 4;
        }

        if (section5) {
            pos = section5;
            esds.specificDescriptorLength = dataView.getUint8(pos);
            pos += 1;

            const decoderSpecificInfo = dataView.getUint16(pos);

            // objectTypeIndex is the first 5 bits of decoderSpecificInfo
            // 63488 is the first 5 of 16 bits
            esds.objectTypeIndex = (decoderSpecificInfo & 63488) >> 11;
            esds.objectType = audioObjectTypes[esds.objectTypeIndex.toString()];

            // sampleRateIndex is the next 4 bits of decoderSpecificInfo
            // 1920 is the first 4 of 11 bits
            esds.sampleRateIndex = (decoderSpecificInfo & 1920) >> 7;
            esds.sampleRate = sampleRates[esds.sampleRateIndex.toString()];

            // channels is the next 4 bits of decoderSpecificInfo
            // 120 is the first 4 of 7 bits
            esds.channels = (decoderSpecificInfo & 120) >> 3;

            // last three bits are uninteresting
            esds.frameLengthFlag = (decoderSpecificInfo & 4) >> 2;
            esds.dependsOnCoreCoder = (decoderSpecificInfo & 2) >> 1;
            esds.extensionFlag = (decoderSpecificInfo & 1);

            pos += 2;
        }

        if (esds.decoderId && esds.objectTypeIndex) {
            if (esds.decoderId === '40') {
                esds.codec = 'mp4a.' + esds.decoderId + '.' + esds.objectTypeIndex;
            } else {
                log({
                    code: 'readEsds',
                    descr: 'unrecognized audio decoder',
                    object: {
                        box: esds,
                        codec: esds.codec,
                        deviceId: mp4Obj?.event?.srcId
                    }
                });
            }
        } else {
            log({
                code: 'readEsds',
                descr: 'audio codec not defined',
                object: {
                    box: esds,
                    codec: esds.codec,
                    deviceId: mp4Obj?.event?.srcId
                }
            });
        }

        mp4Obj.audioCodec = esds.codec;
    }

    function readMp4a(dataView, mp4a, mp4Obj) {
        readChildrenAtPos(dataView, mp4a, mp4a.headerSize + 28, mp4Obj);
        const esds = mp4a.boxes.find(byType, boxTypes.esds);
        if (esds) {
            readEsds(dataView, esds, mp4Obj);
        } else {
            log({
                code: 'readMp4a',
                descr: 'esds box was not found in mp4a box at the expected position',
                object: {
                    box: mp4a,
                    deviceId: mp4Obj?.event?.srcId
                }
            });
        }
    }

    // frame encoding description box
    function readStsd(dataView, stsd, mp4Obj) {
        let pos = stsd.headerSize + stsd.pos;

        readVersionAndFlags(dataView, stsd);
        pos += 4;

        stsd.entryCount = dataView.getUint32(pos);
        pos += 4;

        readChildrenAtPos(dataView, stsd, pos - stsd.pos, mp4Obj);

        const avc1 = stsd.boxes.find(byType, boxTypes.avc1);
        if (avc1) {
            readAvc1(dataView, avc1, mp4Obj);
        }

        const mp4a = stsd.boxes.find(byType, boxTypes.mp4a);
        if (mp4a) {
            readMp4a(dataView, mp4a, mp4Obj);
        }
    }

    // frame timing box
    function readStts(dataView, stts) {
        let pos = stts.headerSize + stts.pos;

        readVersionAndFlags(dataView, stts);
        pos += 4;

        stts.entryCount = dataView.getUint32(pos);
        pos += 4;

        stts.dataPos = pos;
        // entries are 8 bytes each = 4 bytes frame count + 4 bytes duration
    }

    // sample size box
    function readStsz(dataView, stsz) {
        let pos = stsz.headerSize + stsz.pos;

        readVersionAndFlags(dataView, stsz);
        pos += 4;

        stsz.sampleSize = dataView.getUint32(pos);
        pos += 4;

        if (stsz.sampleSize === 0) {
            stsz.entryCount = dataView.getUint32(pos);
            pos += 4;

            stsz.dataPos = pos;
        }
        // entries are 4 bytes each
    }

    // sample/framing to chunk/block box
    function readStsc(dataView, stsc) {
        let pos = stsc.headerSize + stsc.pos;

        readVersionAndFlags(dataView, stsc);
        pos += 4;

        stsc.entryCount = dataView.getUint32(pos);
        pos += 4;

        stsc.dataPos = pos;
        // entries are 12 bytes each
        // 4 bytes block + 4 bytes number of frames
        // 4 bytes samples description id
    }

    // chunk offset box
    // a table of absolute byte offsets in the containing file for each chunk
    function readStco(dataView, stco) {
        let pos = stco.headerSize + stco.pos;

        readVersionAndFlags(dataView, stco);
        pos += 4;

        stco.entryCount = dataView.getUint32(pos);
        pos += 4;

        stco.dataPos = pos;
    }

    // Composition Time to Sample Box
    function readCtts(dataView, ctts) {
        readVersionAndFlags(dataView, ctts);
    }

    // optional sync sample (key/intra frame) box
    function readStss(dataView, stss) {
        let pos = stss.headerSize + stss.pos;

        readVersionAndFlags(dataView, stss);
        pos += 4;

        // number of key frames
        stss.entryCount = dataView.getUint32(pos);
        pos += 4;

        stss.dataPos = pos;
    }

    function readStbl(dataView, stbl, mp4Obj) {
        readChildrenAtPos(dataView, stbl, stbl.headerSize, mp4Obj);

        const stsd = stbl.boxes.find(byType, boxTypes.stsd);
        if (stsd) {
            readStsd(dataView, stsd, mp4Obj);
        }

        if (!mp4Obj.light) {
            const stts = stbl.boxes.find(byType, boxTypes.stts);
            if (stts) {
                readStts(dataView, stts);
            }

            const stsz = stbl.boxes.find(byType, boxTypes.stsz);
            if (stsz) {
                readStsz(dataView, stsz);
            }

            const stsc = stbl.boxes.find(byType, boxTypes.stsc);
            if (stsc) {
                readStsc(dataView, stsc);
            }

            const stco = stbl.boxes.find(byType, boxTypes.stco);
            if (stco) {
                readStco(dataView, stco);
            }

            const ctts = stbl.boxes.find(byType, boxTypes.ctts);
            if (ctts) {
                readCtts(dataView, ctts);
            }

            const stss = stbl.boxes.find(byType, boxTypes.stss);
            if (stss) {
                readStss(dataView, stss);
            }
        }
    }

    function readMinf(dataView, minf, mp4Obj) {
        readChildrenAtPos(dataView, minf, minf.headerSize, mp4Obj);

        if (!mp4Obj.light) {
            // video
            const vmhd = minf.boxes.find(byType, boxTypes.vmhd);
            if (vmhd) {
                readVmhd(dataView, vmhd);
            }

            // audio
            const smhd = minf.boxes.find(byType, boxTypes.smhd);
            if (smhd) {
                readSmhd(dataView, smhd);
            }

            // subtitles
            const nmhd = minf.boxes.find(byType, boxTypes.nmhd);
            if (nmhd) {
                readNmhd(dataView, nmhd);
            }

            const dinf = minf.boxes.find(byType, boxTypes.dinf);
            if (dinf) {
                readDinf(dataView, dinf, mp4Obj);
            }
        }

        const stbl = minf.boxes.find(byType, boxTypes.stbl);
        if (stbl) {
            readStbl(dataView, stbl, mp4Obj);
        }
    }

    function readMdia(dataView, mdia, trackId, mp4Obj) {
        readChildrenAtPos(dataView, mdia, mdia.headerSize, mp4Obj);

        const mdhd = mdia.boxes.find(byType, boxTypes.mdhd);
        if (mdhd) {
            readMdhd(dataView, mdhd, mp4Obj);
        }

        const hdlr = mdia.boxes.find(byType, boxTypes.hdlr);
        if (hdlr) {
            readHdlr(dataView, hdlr);
        }

        // identify the video and audio track IDs and scales
        if (mdhd && hdlr && trackId) {
            if (hdlr.handlerType === 'vide' || hdlr.name === 'VideoHandler') {
                mp4Obj.videoTrackId = trackId;
                mp4Obj.videoTrackScale = mdhd.timeScale;
            }
            if (hdlr.handlerType === 'soun' || hdlr.name === 'SoundHandler') {
                mp4Obj.audioTrackId = trackId;
                mp4Obj.audioTrackScale = mdhd.timeScale;
            }
        }

        const minf = mdia.boxes.find(byType, boxTypes.minf);
        if (minf) {
            readMinf(dataView, minf, mp4Obj);
        }
    }

    function readTrak(dataView, trak, mp4Obj) {
        readChildrenAtPos(dataView, trak, trak.headerSize, mp4Obj);

        const tkhd = trak.boxes.find(byType, boxTypes.tkhd);
        if (tkhd) {
            readTkhd(dataView, tkhd, mp4Obj);
        }

        const mdia = trak.boxes.find(byType, boxTypes.mdia);
        if (mdia) {
            readMdia(dataView, mdia, tkhd?.trackId, mp4Obj);
        }
    }

    function readMvhd(dataView, mvhd, mp4Obj) {
        let pos = mvhd.headerSize + mvhd.pos;

        readVersionAndFlags(dataView, mvhd);
        pos += 4;

        if (mvhd.version === 0) {
            mvhd.created = dataView.getUint32(pos);
            pos += 4;
            mvhd.modified = dataView.getUint32(pos);
            pos += 4;
            mvhd.timeScale = dataView.getUint32(pos);
            pos += 4;
            mvhd.duration = dataView.getUint32(pos);
            pos += 4;

            //pos = 28;
        } else if (mvhd.version === 1) {
            mvhd.createdHi = dataView.getUint32(pos);
            pos += 4;
            mvhd.createdLo = dataView.getUint32(pos);
            pos += 4;
            mvhd.modifiedHi = dataView.getUint32(pos);
            pos += 4;
            mvhd.modifiedLo = dataView.getUint32(pos);
            pos += 4;
            mvhd.timeScale = dataView.getUint32(pos);
            pos += 4;
            mvhd.durationHi = dataView.getUint32(pos);
            pos += 4;
            mvhd.durationLo = dataView.getUint32(pos);
            pos += 4;

            //pos = 40;
        } else {
            log({
                code: 'readMvhd',
                descr: 'bad mvhd version',
                object: {
                    box: mvhd,
                    deviceId: mp4Obj?.event?.srcId
                }
            });
        }

        mvhd.rate = [];
        mvhd.rate[0] = dataView.getUint16(pos);
        pos += 2;
        mvhd.rate[1] = dataView.getUint16(pos);
        pos += 2;
        mvhd.volume = [];
        mvhd.volume[0] = dataView.getUint8(pos);
        pos += 1;
        mvhd.volume[1] = dataView.getUint8(pos);
        pos += 1;
        mvhd.reserved = [];

        let i = 0;
        while (i < 10) {
            mvhd.reserved[i] = dataView.getUint8(pos);
            pos += 1;
            i += 1;
        }
        mvhd.matrix = [];
        i = 0;
        while (i < 36) {
            mvhd.matrix[i] = dataView.getUint8(pos);
            pos += 1;
            i += 1;
        }
        mvhd.predefined = [];
        i = 0;
        while (i < 24) {
            mvhd.predefined[i] = dataView.getUint8(pos);
            pos += 1;
            i += 1;
        }
        mvhd.nextTrackId = dataView.getUint32(pos);
    }

    function atos(arr) {
        let result = '';
        let i = 0;
        while (i < arr.length) {
            result += String.fromCodePoint(arr[i]);
            i += 1;
        }
        return result;
    }

    function readData(dataView, data, parent /*, mp4Obj*/) {
        let pos = data.headerSize + data.pos;

        //readVersionAndFlags(dataView, data);
        //pos += 4;

        // some box types do not have text content, so ignore.
        if (parent.type === boxTypes.covr) {
            return;
        }

        data.dataTypeIndex = dataView.getUint32(pos);
        data.dataType = dataTypes[data.dataTypeIndex.toString()];
        pos += 4;

        data.country = dataView.getUint16(pos);
        pos += 2;

        data.langauge = dataView.getUint16(pos);
        pos += 2;

        // get text of the data box
        const length = Math.min(data.size + data.pos - pos, 255);
        let byteArray = new Uint8Array(length);
        let i = 0;
        while (i < length) {
            byteArray[i] = dataView.getUint8(pos + i);
            i += 1;
        }

        data.value = atos(byteArray);
    }

    function readIlst(dataView, ilst, mp4Obj) {
        readChildrenAtPos(dataView, ilst, ilst.headerSize, mp4Obj);

        ilst.boxes.forEach(function(box) {
            readChildrenAtPos(dataView, box, box.headerSize, mp4Obj);

            // each box here could contain 'itif' or 'name' boxes

            const datas = box.boxes.filter(byType, boxTypes.data);
            if (Array.isArray(datas)) {
                datas.forEach(function(data) {
                    readData(dataView, data, box, mp4Obj);
                });
            }

        });
    }

    function readMetaHdlr(dataView, hdlr) {
        let pos = hdlr.headerSize + hdlr.pos;

        readVersionAndFlags(dataView, hdlr);
        pos += 4;

        // should be 0
        hdlr.predefined = dataView.getUint32(pos);
        pos += 4;

        hdlr.hdlrType = String.fromCharCode(dataView.getUint8(pos))
                + String.fromCharCode(dataView.getUint8(pos + 1))
                + String.fromCharCode(dataView.getUint8(pos + 2))
                + String.fromCharCode(dataView.getUint8(pos + 3));
        pos += 4;

        hdlr.reserved = [];
        hdlr.reserved.push(dataView.getUint32(pos));
        pos += 4;
        hdlr.reserved.push(dataView.getUint32(pos));
        pos += 4;
        hdlr.reserved.push(dataView.getUint32(pos));
        pos += 4;

        // get name - may be 0x00 or blank
        const length = Math.min(hdlr.size + hdlr.pos - pos, 255);
        if (length > 1) {
            let byteArray = new Uint8Array(length);
            let i = 0;
            while (i < length) {
                byteArray[i] = dataView.getUint8(pos + i);
                i += 1;
            }
            hdlr.name = atos(byteArray);
        }
    }

    function readMeta(dataView, meta, mp4Obj) {
        let pos = meta.headerSize + meta.pos;

        readVersionAndFlags(dataView, meta);
        pos += 4;

        readChildrenAtPos(dataView, meta, pos - meta.pos, mp4Obj);

        const ilst = meta.boxes.find(byType, boxTypes.ilst);
        if (ilst) {
            readIlst(dataView, ilst, mp4Obj);
        }

        const hdlr = meta.boxes.find(byType, boxTypes.hdlr);
        if (hdlr) {
            readMetaHdlr(dataView, hdlr, mp4Obj);
        }
    }

    function readUdta(dataView, udta, mp4Obj) {
        readChildrenAtPos(dataView, udta, udta.headerSize, mp4Obj);

        const meta = udta.boxes.find(byType, boxTypes.meta);
        if (meta) {
            readMeta(dataView, meta, mp4Obj);
        }
    }

    function readMoov(dataView, moov, mp4Obj) {
        readChildrenAtPos(dataView, moov, moov.headerSize, mp4Obj);

        if (!mp4Obj.light) {
            const mvhd = moov.boxes.find(byType, boxTypes.mvhd);
            if (mvhd) {
                readMvhd(dataView, mvhd, mp4Obj);
            }

            const udta = moov.boxes.find(byType, boxTypes.udta);
            if (udta) {
                readUdta(dataView, udta, mp4Obj);
            }
        }

        const traks = moov.boxes.filter(byType, boxTypes.trak);
        traks.forEach(function(trak) {
            readTrak(dataView, trak, mp4Obj);
        });
    }

    function readSidx(dataView, sidx, mp4Obj) {
        let pos = sidx.headerSize + sidx.pos;

        readVersionAndFlags(dataView, sidx);
        pos += 4;

        // should match trackId in tkhd
        sidx.referenceId = dataView.getUint32(pos);
        pos += 4;

        // should match timescale in mdhd
        sidx.timescale = dataView.getUint32(pos);
        pos += 4;

        let earliestPresentationTime;
        let firstOffset;
        if (sidx.version === 0) {
            earliestPresentationTime = dataView.getUint32(pos);
            pos += 4;

            firstOffset = dataView.getUint32(pos);
            pos += 4;
        } else {
            earliestPresentationTime = uInt64(dataView.getUint32(pos), dataView.getUint32(pos + 4));
            pos += 8;

            firstOffset = uInt64(dataView.getUint32(pos), dataView.getUint32(pos + 4));
            pos += 8;
        }

        // reserved 16 bits
        pos += 2;

        sidx.referenceCount = dataView.getUint16(pos);
        pos += 2;

        let unscaledStartTime = earliestPresentationTime;
        let startByte = sidx.pos + sidx.size + firstOffset;
        let header;
        let sap;
        let reference;
        sidx.references = [];

        let i = 0;
        while (i < sidx.referenceCount) {
            reference = { index: i };

            header = dataView.getUint32(pos);
            pos += 4;

            // type is first bit, size is other bits
            reference.referenceType = (header & 0x80000000) >>> 31;
            reference.referenceSize = header & 0x7FFFFFFF;

            if (reference.referenceType === 1) {
                log({
                    code: 'readSidx',
                    descr: 'unsupported sidx reference version',
                    object: {
                        box: sidx,
                        deviceId: mp4Obj?.event?.srcId
                    }
                });
            }

            reference.subSegmentDuration = dataView.getUint32(pos);
            pos += 4;

            sap = dataView.getUint32(pos);
            pos += 4;

            // startsWith is first bit, type is next 3 bits, delta is the rest
            reference.startsWithSap = (sap & 0x80000000) >>> 31;
            reference.sapType = (sap & 0x70000000) >>> 28;
            reference.sapDelta = sap & 0x0FFFFFFF;

            reference.startTime = (unscaledStartTime / sidx.timescale)/*- scaledPresentationTimeOffset*/;
            reference.finishTime = ((unscaledStartTime + reference.subSegmentDuration) / sidx.timescale)/*- scaledPresentationTimeOffset*/;
            reference.startByte = startByte;
            reference.finishByte = startByte + reference.referenceSize - 1;

            unscaledStartTime += reference.subSegmentDuration;
            startByte += reference.referenceSize;

            sidx.references.push(reference);
            i += 1;
        }
    }

    function readTfhd(dataView, tfhd) {
        let pos = tfhd.headerSize + tfhd.pos;

        readVersionAndFlags(dataView, tfhd);
        pos += 4;

        // parse flags
        tfhd.isBaseDataOffsetAvailable = (1 & tfhd.flags[2]) > 0;
        tfhd.isSampleDescriptionIndexAvailable = (2 & tfhd.flags[2]) > 0;
        tfhd.isDefaultSampleDurationAvailable = (8 & tfhd.flags[2]) > 0;
        tfhd.isDefaultSampleSizeAvailable = (16 & tfhd.flags[2]) > 0;
        tfhd.isDefaultSampleFlagsAvailable = (32 & tfhd.flags[2]) > 0;

        tfhd.trackId = dataView.getUint32(pos);
        pos += 4;
        if (tfhd.isBaseDataOffsetAvailable) {
            tfhd.offsetHi = dataView.getUint32(pos);
            pos += 4;
            tfhd.offsetLo = dataView.getUint32(pos);
            pos += 4;
        }

        if (tfhd.isSampleDescriptionIndexAvailable) {
            tfhd.sampleDescriptionIndex = dataView.getUint32(pos);
            pos += 4;
        }

        if (tfhd.isDefaultSampleDurationAvailable) {
            tfhd.defaultSampleDuration = dataView.getUint32(pos);
            pos += 4;
        }

        if (tfhd.isDefaultSampleSizeAvailable) {
            tfhd.defaultSampleSize = dataView.getUint32(pos);
            pos += 4;
        }
        if (tfhd.isDefaultSampleFlagsAvailable) {
            tfhd.defaultSampleFlags = dataView.getUint32(pos);
        }
    }

    // Track Fragment Decode Time
    // Required for HTML5 players, although technically not required.
    function readTfdt(dataView, tfdt, mp4Obj, trackId) {
        let pos = tfdt.headerSize + tfdt.pos;

        readVersionAndFlags(dataView, tfdt);
        pos += 4;

        const baseMediaDecodeTimePos = pos;

        if (tfdt.version === 0) {
            tfdt.baseMediaDecodeTime = dataView.getUint32(baseMediaDecodeTimePos);
        } else if (tfdt.version === 1) {
            tfdt.baseMediaDecodeTimeHi = dataView.getUint32(baseMediaDecodeTimePos);
            tfdt.baseMediaDecodeTimeLo = dataView.getUint32(baseMediaDecodeTimePos + 4);
        } else {
            log({
                code: 'readTfdt',
                descr: 'unsupported tfdt version',
                object: {
                    box: tfdt,
                    deviceId: mp4Obj?.event?.srcId
                }
            });
        }

        // update baseMediaDecodeTimes if needed
        // This is necessary in Chrome to not overwrite existing timeline,
        // and may be necessary for all clients.
        if (mp4Obj.sequenceTrackTimes) {
            if (tfdt.version === 0) {
                dataView.setUint32(baseMediaDecodeTimePos, tfdt.baseMediaDecodeTime
                        + mp4Obj.sequenceTrackTimes[trackId]);
                tfdt.baseMediaDecodeTime = dataView.getUint32(baseMediaDecodeTimePos);
            } else if (tfdt.version === 1) {
                // this does not support 64-bit, but it is necessary and does get used
                // since tfdt.version == 1 does get used
                dataView.setUint32(baseMediaDecodeTimePos + 4, tfdt.baseMediaDecodeTimeLo
                        + mp4Obj.sequenceTrackTimes[trackId]);
                tfdt.baseMediaDecodeTimeLo = dataView.getUint32(baseMediaDecodeTimePos + 4);
            }
        }
    }

    // Track Fragment Run
    function readTrun(dataView, trun, mp4Obj) {
        let pos = trun.headerSize + trun.pos;

        readVersionAndFlags(dataView, trun);
        pos += 4;

        readVersionAndFlags(dataView, trun);

        // parse flags
        trun.isDataOffsetAvailable = (1 & trun.flags[2]) > 0;
        trun.isFirstSampleFlagsAvailable = (4 & trun.flags[2]) > 0;
        trun.isSampleDurationAvailable = (1 & trun.flags[1]) > 0;
        trun.isSampleSizeAvailable = (2 & trun.flags[1]) > 0;
        trun.isSampleFlagsAvailable = (4 & trun.flags[1]) > 0;
        trun.isSampleCompositionOffsetAvailable = (8 & trun.flags[1]) > 0;

        if (trun.isSampleFlagsAvailable && trun.isFirstSampleFlagsAvailable) {
            log({
                code: 'readTrun',
                descr: 'trun error - broken stream',
                object: {
                    box: trun,
                    deviceId: mp4Obj?.event?.srcId
                }
            });
        }

        trun.sampleCount = dataView.getUint32(pos);
        pos += 4;
        if (trun.isDataOffsetAvailable) {
            // signed value
            trun.offset = dataView.getInt32(pos);
            pos += 4;
        }
        if (trun.isFirstSampleFlagsAvailable) {
            trun.firstSampleFlags = dataView.getUint32(pos);
            pos += 4;
        }

        trun.samples = [];
        trun.sampleDurationSum = 0;
        let i = 0;
        let sample;
        while (i < trun.sampleCount) {
            sample = {};
            if (trun.isSampleDurationAvailable) {
                sample.duration = dataView.getUint32(pos);
                trun.sampleDurationSum += sample.duration;
                pos += 4;
            }
            if (trun.isSampleSizeAvailable) {
                sample.size = dataView.getUint32(pos);
                pos += 4;
            }
            if (trun.isSampleFlagsAvailable) {
                sample.flags = dataView.getUint32(pos);
                pos += 4;
            }
            if (trun.isSampleCompositionOffsetAvailable) {
                if (trun.version === 0) {
                    sample.compositionTimeOffset = dataView.getUint32(pos);
                }
                if (trun.version === 1) {
                    sample.compositionTimeOffset = dataView.getInt32(pos);
                }
                pos += 4;
            }
            trun.samples.push(sample);
            i += 1;
        }
    }

    // Track Fragment
    function readTraf(dataView, traf, mp4Obj) {
        readChildrenAtPos(dataView, traf, traf.headerSize, mp4Obj);

        const tfhd = traf.boxes.find(byType, boxTypes.tfhd);
        if (tfhd) {
            readTfhd(dataView, tfhd);
        }

        const tfdt = traf.boxes.find(byType, boxTypes.tfdt);
        if (tfdt) {
            readTfdt(dataView, tfdt, mp4Obj, tfhd.trackId);
        }

        const trun = traf.boxes.find(byType, boxTypes.trun);
        if (trun) {
            readTrun(dataView, trun, mp4Obj);
        }

        // find what should be the baseMediaDecodeTime of this track in the next moof
        if (trun.sampleDurationSum) {
            mp4Obj.nextSequenceTrackTimes[tfhd.trackId] =
                    (tfdt.baseMediaDecodeTime || tfdt.baseMediaDecodeTimeLo)
                    + trun.sampleDurationSum;
        } else {
            mp4Obj.nextSequenceTrackTimes[tfhd.trackId] =
                    (tfdt.baseMediaDecodeTime || tfdt.baseMediaDecodeTimeLo)
                    + tfhd.defaultSampleDuration * trun.sampleCount;
        }

        // add the track duration to the totals
        if (tfhd && trun) {
            if (tfhd.trackId === mp4Obj.videoTrackId && mp4Obj.videoTrackScale) {
                mp4Obj.videoDuration += trun.sampleDurationSum / mp4Obj.videoTrackScale;
            }
            if (tfhd.trackId === mp4Obj.audioTrackId && mp4Obj.audioTrackScale) {
                mp4Obj.audioDuration += trun.sampleDurationSum / mp4Obj.audioTrackScale;
            }
        }
    }

    function readMfhd(dataView, mfhd, mp4Obj, moofIndex) {
        // reset sequence number if needed
        if (Number.isInteger(mp4Obj.firstSequenceNumber)) {
            dataView.setUint32(mfhd.pos + 12, mp4Obj.firstSequenceNumber + moofIndex);
        }

        mfhd.sequenceNumber = dataView.getUint32(mfhd.pos + 12);

        mp4Obj.lastSequenceNumber = mfhd.sequenceNumber;
    }

    function readMoof(dataView, moof, mp4Obj, moofIndex) {
        readChildrenAtPos(dataView, moof, moof.headerSize, mp4Obj);

        if (moofIndex === 0) {
            mp4Obj.firstMoofPos = moof.pos;
        }

        const mfhd = moof.boxes.find(byType, boxTypes.mfhd);
        if (mfhd) {
            readMfhd(dataView, mfhd, mp4Obj, moofIndex);
        }

        const trafs = moof.boxes.filter(byType, boxTypes.traf);
        trafs.forEach(function(traf) {
            readTraf(dataView, traf, mp4Obj);
        });
    }

    function readMfra(dataView, mfra, mp4Obj) {
        //readDebugInfo(dataView, mfra);
        readChildrenAtPos(dataView, mfra, mfra.headerSize, mp4Obj);
    }

    // _mp4Util.fragmentBytes = function(mp4) {
    //     mp4.byteArray = mp4.byteArray.subArray(mp4.obj.firstMoofPos);
    // };

    /**
     * @param {Object} spec
     * @param spec.arrayBuffer
     * @param spec.fragmentBytesOnly
     * @param spec.firstSequenceNumber
     * @param spec.noMfra
     * @param spec.sequenceTrackTimes
     * @returns {Mp4} an mp4 object representing a parsed mp4 file
     * @name parse
     * @method
     */
    _mp4Util.parse = function(spec) {
        const result = {
            arrayBuffer: spec.arrayBuffer,
            byteArray: new Uint8Array(spec.arrayBuffer),
            dataView: new DataView(spec.arrayBuffer),
            obj: undefined
        };

        result.obj = {
            size: result?.byteArray?.byteLength,
            firstSequenceNumber: spec.firstSequenceNumber,
            nextSequenceTrackTimes: {},
            sequenceTrackTimes: spec.sequenceTrackTimes,
            light: spec.light,
            event: spec.event,
            videoDuration: 0,
            audioDuration: 0,
            videoTrackScale: 0,
            audioTrackScale: 0,
            videoTrackId: 0,
            audioTrackId: 0
        };

        readChildrenAtPos(result.dataView, result.obj, undefined, result.obj);

        if (!result.obj.light) {
            const ftyp = result.obj.boxes.find(byType, boxTypes.ftyp);
            if (ftyp) {
                readFtyp(result.dataView, ftyp);
            }
        }

        const moov = result.obj.boxes.find(byType, boxTypes.moov);
        if (moov) {
            readMoov(result.dataView, moov, result.obj);
        }

        const sidx = result.obj.boxes.find(byType, boxTypes.sidx);
        if (sidx) {
            readSidx(result.dataView, sidx, result.obj);
        }

        const moofs = result.obj.boxes.filter(byType, boxTypes.moof);
        if (moofs) {
            moofs.forEach(function(moof, moofIndex) {
                readMoof(result.dataView, moof, result.obj, moofIndex);
            });
        }

        const mfra = result.obj.boxes.find(byType, boxTypes.mfra);
        if (mfra) {
            readMfra(result.dataView, mfra, result.obj);
        }

        if (mfra && spec.noMfra) {
            result.byteArray = result.byteArray.subarray(0, mfra.pos);
        }

        if (spec.fragmentBytesOnly && result.obj.firstMoofPos) {
            result.byteArray = result.byteArray.subarray(result.obj.firstMoofPos);
        }

        delete result.sequenceTrackTimes;
        delete result.light;
        delete result.obj.audioTrackId;
        delete result.obj.videoTrackId;
        delete result.obj.audioTrackScale;
        delete result.obj.videoTrackScale;

        return result;
    };

    function shallowCopyBox(source) {
        const result = {};

        Object.keys(source).forEach(function(key) {
            if (key !== 'boxes') {
                if (typeof source[key] === 'object') {
                    if (Array.isArray(source[key])) {
                        result[key] = [];
                    } else {
                        result[key] = {};
                    }
                    util.merge(result[key], source[key]);
                } else {
                    result[key] = source[key];
                }
            }
        });

        return result;
    }

    function writeFtyp(in1, in2, out, state) {
        const ftyp1 = in1.obj.boxes.find(byType, boxTypes.ftyp);

        if (ftyp1) {
            const ftypOut = shallowCopyBox(ftyp1);
            out.obj.boxes.push(ftypOut);
            out.byteArray.set(in1.byteArray.subarray(ftyp1.pos, ftyp1.size), state.pos);

            state.pos = ftypOut.pos + ftypOut.size;
        }
    }

    function writeMdat(in1, in2, out, state) {
        const mdat1 = in1.obj.boxes.find(byType, boxTypes.mdat);
        const mdat2 = in2.obj.boxes.find(byType, boxTypes.mdat);

        const mdatOut = shallowCopyBox(mdat1);
        out.obj.boxes.push(mdatOut);

        // pos
        mdatOut.pos = state.pos;

        // mdat1 header and data
        out.byteArray.set(in1.byteArray.subarray(mdat1.pos, mdat1.pos + mdat1.size), state.pos);
        state.pos += mdat1.size;

        // mdat2 data
        out.byteArray.set(in2.byteArray.subarray(mdat2.pos + 8, mdat2.pos + mdat2.size - 8), state.pos);
        state.pos += mdat2.size - 8;

        // write new size = size of both minus the header of one
        mdatOut.size = mdat1.size + mdat2.size - 8;
        out.dataView.setUint32(mdat1.pos, mdatOut.size);
    }

    function writeMvhd(in1, in2, out, state) {
        state.mvhd1 = state.moov1.boxes.find(byType, boxTypes.mvhd);
        state.mvhd2 = state.moov2.boxes.find(byType, boxTypes.mvhd);
        state.mvhdOut = shallowCopyBox(state.mvhd1);
        state.moovOut.boxes.push(state.mvhdOut);

        // time scale must match
        if (state.mvhd1.timeScale !== state.mvhd2.timeScale) {
            throw 'files have different mvhd time scale';
        }

        state.mvhdOut.pos = state.pos;
        state.mvhdOut.duration = state.mvhd1.duration + state.mvhd2.duration;

        // copy from in1 except for new duration
        out.byteArray.set(in1.byteArray.subarray(state.mvhd1.pos, state.mvhd1.pos + state.mvhd1.size), state.mvhdOut.pos);
        out.dataView.setUint32(state.mvhdOut.pos + 24, state.mvhdOut.duration);

        state.pos = state.mvhdOut.pos + state.mvhdOut.size;
    }

    function writeIods(in1, in2, out, state) {
        state.iods1 = state.moov1.boxes.find(byType, boxTypes.iods);
        state.iods2 = state.moov2.boxes.find(byType, boxTypes.iods);

        state.iodsOut = shallowCopyBox(state.iods1);
        state.moovOut.boxes.push(state.iodsOut);

        state.iodsOut.pos = state.pos;

        // copy from in1
        out.byteArray.set(in1.byteArray.subarray(state.iods1.pos, state.iods1.pos + state.iods1.size), state.iodsOut.pos);

        state.pos += state.iodsOut.size;
    }

    function writeTkhd(in1, in2, out, state) {
        const tkhd1 = state.trak1.boxes.find(byType, boxTypes.tkhd);
        const tkhd2 = state.trak2.boxes.find(byType, boxTypes.tkhd);
        if (tkhd1 && tkhd2) {
            const tkhd = shallowCopyBox(tkhd1);
            state.trakOut.boxes.push(tkhd);

            tkhd.pos = state.pos;

            tkhd.duration = tkhd1.duration + tkhd2.duration;
            out.byteArray.set(in1.byteArray.subarray(tkhd1.pos, tkhd1.pos + tkhd1.size), tkhd.pos);
            out.dataView.setUint32(tkhd.pos + 28, tkhd.duration);

            state.pos = tkhd.pos + tkhd.size;
        }
    }

    function writeMinf(in1, in2, out, state) {
        const minf1 = state.mdia1.boxes.find(byType, boxTypes.minf);
        const minf2 = state.mdia2.boxes.find(byType, boxTypes.minf);
        if (minf1 && minf2) {
            const minfOut = shallowCopyBox(minf1);
            state.minf1 = minf1;
            state.minf2 = minf2;
            state.minfOut = minfOut;

            state.mdiaOut.boxes.push(minfOut);
            minfOut.boxes = [];
            minfOut.pos = state.pos;

            // children
            state.pos += 8;
            //            writeSmhd(in1, in2, out, state);
            //            writeDinf(in1, in2, out, state);
            //            writeStbl(in1, in2, out, state);

            // size
            const lastBox = minfOut.boxes[minfOut.boxes.length - 1];
            if (lastBox) {
                minfOut.size = lastBox.pos + lastBox.size - minfOut.pos;
            } else {
                minfOut.size = 8;
            }
            out.dataView.setUint32(minfOut.pos, minfOut.size);

            // type
            out.byteArray.set(in1.byteArray.subarray(minf1.pos + 4, minf1.pos + 8), minfOut.pos + 4);

            state.pos = minfOut.pos + minfOut.size;
        }
    }

    function writeHdlr(in1, in2, out, state) {
        const hdlr1 = state.mdia1.boxes.find(byType, boxTypes.hdlr);
        const hdlr2 = state.mdia1.boxes.find(byType, boxTypes.hdlr);
        if (hdlr1 && hdlr2) {
            const hdlrOut = shallowCopyBox(hdlr1);
            state.mdiaOut.boxes.push(hdlrOut);

            hdlrOut.pos = state.pos;

            out.byteArray.set(in1.byteArray.subarray(hdlr1.pos, hdlr1.pos + hdlr1.size), hdlrOut.pos);

            state.pos = hdlrOut.pos + hdlrOut.size;
        }
    }

    function writeMdhd(in1, in2, out, state) {
        const mdhd1 = state.mdia1.boxes.find(byType, boxTypes.mdhd);
        const mdhd2 = state.mdia1.boxes.find(byType, boxTypes.mdhd);
        if (mdhd1 && mdhd2) {
            const mdhdOut = shallowCopyBox(mdhd1);
            state.mdiaOut.boxes.push(mdhdOut);

            mdhdOut.pos = state.pos;

            // time scale must match
            if (mdhd1.timeScale !== mdhd2.timeScale) {
                throw 'files have different mdhd time scale';
            }

            mdhdOut.duration = mdhd1.duration + mdhd2.duration;
            out.byteArray.set(in1.byteArray.subarray(mdhd1.pos, mdhd1.pos + mdhd1.size), mdhdOut.pos);
            out.dataView.setUint32(mdhdOut.pos + 24, mdhdOut.duration);

            state.pos = mdhdOut.pos + mdhdOut.size;
        }
    }

    function writeMdia(in1, in2, out, state) {
        const mdia1 = state.trak1.boxes.find(byType, boxTypes.mdia);
        const mdia2 = state.trak2.boxes.find(byType, boxTypes.mdia);
        if (mdia1 && mdia2) {
            const mdiaOut = shallowCopyBox(mdia1);
            state.mdia1 = mdia1;
            state.mdia2 = mdia2;
            state.mdiaOut = mdiaOut;

            state.trakOut.boxes.push(mdiaOut);
            mdiaOut.boxes = [];
            mdiaOut.pos = state.pos;

            // children
            state.pos += 8;
            writeMdhd(in1, in2, out, state);
            writeHdlr(in1, in2, out, state);
            writeMinf(in1, in2, out, state);

            // size
            const lastBox = mdiaOut.boxes[mdiaOut.boxes.length - 1];
            if (lastBox) {
                mdiaOut.size = lastBox.pos + lastBox.size - mdiaOut.pos;
            } else {
                mdiaOut.size = 8;
            }
            out.dataView.setUint32(mdiaOut.pos, mdiaOut.size);

            // type
            out.byteArray.set(in1.byteArray.subarray(mdia1.pos + 4, mdia1.pos + 8), mdiaOut.pos + 4);

            state.pos = mdiaOut.pos + mdiaOut.size;
        }
    }

    function writeTrak(in1, in2, out, state) {
        state.trakOut = shallowCopyBox(state.trak1);
        state.trakOut.boxes = [];
        state.moovOut.boxes.push(state.trakOut);

        state.trakOut.pos = state.pos;

        // write children
        state.pos += 8;
        writeTkhd(in1, in2, out, state);
        writeMdia(in1, in2, out, state);

        // trak size
        const lastBox = state.trakOut.boxes[state.trakOut.boxes.length - 1];
        if (lastBox) {
            state.trakOut.size = lastBox.pos + lastBox.size - state.trakOut.pos;
        } else {
            state.trakOut.size = 8;
        }
        out.dataView.setUint32(state.trakOut.pos, state.trakOut.size);

        // trak type
        out.byteArray.set(in1.byteArray.subarray(state.trak1.pos + 4, state.trak1.pos + 8), state.trakOut.pos + 4);

        state.pos = state.trakOut.pos + state.trakOut.size;
    }

    function writeMoov(in1, in2, out, state) {
        state.moov1 = in1.obj.boxes.find(byType, boxTypes.moov);
        state.moov2 = in2.obj.boxes.find(byType, boxTypes.moov);
        state.moovOut = shallowCopyBox(state.moov1);
        state.moovOut.boxes = [];
        out.obj.boxes.push(state.moovOut);

        // pos
        state.moovOut.pos = state.pos;

        // write children
        state.pos += 8;
        writeMvhd(in1, in2, out, state);
        writeIods(in1, in2, out, state);

        const videoTraks1 = [];
        const audioTraks1 = [];
        const videoTraks2 = [];
        const audioTraks2 = [];

        let traks = state.moov1.boxes.filter(byType, boxTypes.trak);
        traks.forEach(function(trak) {
            const mdia = trak.boxes.find(byType, boxTypes.mdia);
            if (mdia) {
                const hdlr = mdia.boxes.find(byType, boxTypes.hdlr);
                if (hdlr) {
                    if (hdlr.handlerType === handlerTypes.vide) {
                        videoTraks1.push(trak);
                    } else if (hdlr.handlerType === handlerTypes.soun) {
                        audioTraks1.push(trak);
                    } else {
                        log({
                            code: 'writeMoov',
                            descr: 'found unsupported moov1 trak mdia hdlr handler type',
                            object: hdlr
                        });
                    }
                }
            }
        });

        traks = state.moov2.boxes.filter(byType, boxTypes.trak);
        traks.forEach(function(trak) {
            const mdia = trak.boxes.find(byType, boxTypes.mdia);
            if (mdia) {
                const hdlr = mdia.boxes.find(byType, boxTypes.hdlr);
                if (hdlr) {
                    if (hdlr.handlerType === handlerTypes.vide) {
                        videoTraks2.push(trak);
                    } else if (hdlr.handlerType === handlerTypes.soun) {
                        audioTraks2.push(trak);
                    } else {
                        log({
                            code: 'writeMoov',
                            descr: 'found unsupported moov2 trak mdia hdlr handler type',
                            object: hdlr
                        });
                    }
                }
            }
        });

        if (videoTraks1.length === 1 && videoTraks2.length === 1) {
            state.trak1 = videoTraks1[0];
            state.trak2 = videoTraks2[0];
            state.trakType = handlerTypes.vide;
            writeTrak(in1, in2, out, state);
        } else {
            log({
                code: 'writeMoov',
                descr: 'files did not each have exactly 1 video trak, so video traks were not merged',
                object: {
                    videoTraks1: videoTraks1,
                    videoTraks2: videoTraks2
                }
            });
        }

        if (audioTraks1.length === 1 && audioTraks2.length === 1) {
            state.trak1 = audioTraks1[0];
            state.trak2 = audioTraks2[0];
            state.trakType = handlerTypes.soun;
            writeTrak(in1, in2, out, state);
        } else {
            log({
                code: 'writeMoov',
                descr: 'files did not each have exactly 1 audio trak, so audio traks were not merged',
                object: {
                    audioTraks1: audioTraks1,
                    audioTraks2: audioTraks2
                }
            });
        }

        delete state.trak1;
        delete state.trak2;
        delete state.trakType;

        // moov size
        const lastBox = state.moovOut.boxes[state.moovOut.boxes.length - 1];
        if (lastBox) {
            state.moovOut.size = lastBox.pos + lastBox.size - state.moovOut.pos;
        } else {
            state.moovOut.size = 8;
        }
        out.dataView.setUint32(state.moovOut.pos, state.moovOut.size);

        // moov type
        out.byteArray.set(in1.byteArray.subarray(state.moov1.pos + 4, state.moov1.pos + 8), state.moovOut.pos + 4);

        // pos
        state.pos = state.moovOut.pos + state.moovOut.size;
    }

    /**
     * returns an mp4 object that combines two mp4 object inputs
     * @param {Mp4} in1 Video 1
     * @param {Mp4} in2 Video 2
     * @returns {Mp4} The merged video object
     * @name merge
     * @method
     */
    _mp4Util.merge = function(in1, in2) {
        const out = { obj: { boxes: [], pos: 0 } };
        out.arrayBuffer = new ArrayBuffer(in1?.byteArray?.byteLength + in2?.byteArray?.byteLength);
        out.byteArray = new Uint8Array(out.arrayBuffer);
        out.dataView = new DataView(out.arrayBuffer);

        const state = { pos: 0 };

        writeFtyp(in1, in2, out, state);
        writeMdat(in1, in2, out, state);
        writeMoov(in1, in2, out, state);

        // reset length to actual length
        out.arrayBuffer = out.arrayBuffer.slice(0, state.pos);
        out.obj.size = state.pos;

        return out;
    };

    return Object.freeze(_mp4Util);
}

export default createMp4Util;








